/*
 * File: 处理流式请求
 */
import { attempt, isObject, isError } from 'lodash';
import config from '../config/user.env';

/**
 * 创建流式请求
 * @param {object} params - 配置参数
 * @param {string} params.url - API地址
 * @param {Function} params.callback - 数据处理回调函数
 * @param {object} params.extraHeaders - 额外请求头
 * @param {string} params.method - 请求方法
 * @param {object} params.body - 请求体
 * @param {string} params.delimiter - 数据分隔符
 * @returns {object} 包含abort方法和promise的对象
 */
/* eslint-disable max-lines-per-function */
export default function streamHttp({
    url,
    callback,
    extraHeaders = {},
    method = 'POST',
    body,
    delimiter = '\n'
}) {
    // 创建ac
    const ac = new AbortController();
    const signal = ac?.signal;

    // 合并请求头
    const headers = {
        'Content-Type': 'application/json;charset=UTF-8',
        accept: 'text/event-stream',
        ...extraHeaders
    };

    const parseRes = (res) => {
        const streamData = attempt(JSON.parse, res.match(/:([\s\S]*)$/)?.[1]);
        if (!isError(streamData) && isObject(streamData)) {
            return streamData;
        }
        return null;
    };

    // 处理流数据
    const processStream = async (reader) => {
        const decoder = new TextDecoder();
        let buffer = '';

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    // 处理最后剩余数据
                    const data = parseRes(buffer);
                    data && callback(data);
                    return;
                }

                buffer += decoder.decode(value, { stream: true });
                // 按分隔符分割处理数据
                let index = buffer.indexOf(delimiter);
                while (index !== -1) {
                    const chunk = buffer.slice(0, index);
                    buffer = buffer.slice(index + delimiter.length);
                    const data = parseRes(chunk);
                    data && callback(data);
                    index = buffer.indexOf(delimiter);
                }
            }
        } finally {
            reader.releaseLock();
        }
    };

    // AI测试环境只有uat，因此需要配置其他测试环境前缀
    const prefix = config.env === 'PROD' ? config.apiUrl : 'https://anapi-uat.annto.com/';

    // 创建请求Promise
    const requestPromise = fetch(prefix + url, {
        method,
        headers,
        signal,
        ...(method === 'GET' ? {} : { body: JSON.stringify(body) })
    })
        .then((response) => {
            if (!response.ok) throw new Error(`HTTP错误 ${response.status}`);

            return response.body?.getReader();
        })
        .then((reader) => processStream(reader))
        .catch((error) => {
            if (error.name !== 'AbortError') throw error;
        });

    return {
        // 中止方法
        abort: () => {
            if (ac) ac.abort();
        },
        // 请求Promise
        promise: requestPromise,
        controller: ac
    };
}
